import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import { Alert, Box, useMediaQuery, useTheme } from "@mui/material";
import { MapContainer, TileLayer, useMap, useMapEvents } from "react-leaflet";
import ToolBarMaps from "../../../../../componentes/easyMaps/toolbarMapsPublicRp";
import L from "leaflet";
import "../../../../../componentes/leaflet-migrations";
import "../../../../../componentes/leaflet-fullscreen/Leaflet.fullscreen";
import "leaflet-routing-machine";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import TitleBar from "../../../../../componentes/easyMaps/titleBarPublic";
//import * as turf from "@turf/turf";
import ContextMenu from "../../../../../componentes/easyMaps/contextMenu";
import AlertModal from "../../../../../componentes/alertModal";
import DescriptionBar from "../../../../../componentes/easyMaps/descriptionBar";
import { Link, useOutletContext } from "react-router-dom";

import ClickDispatcher from "./components/clickDispatcher";
import PixiOverlay from "./pixiOverlayIcons";
import useTextures from "./hooks/useTextures";
import { PolylineCustom } from "../../geolocalizadoFTTH/leaflatMapa/polyLine";
import RightBarEasyMapsOnus from "../../../../../componentes/easyMaps/rightBarEasyMapsOnus";
import { Close } from "@mui/icons-material";
import RulerComponent from "../../../../../componentes/easyMaps/rulerPoints";

dayjs.extend(utc);
dayjs.extend(timezone);

const LeafletMapa = forwardRef((props, ref) => {
  const {
    title,
    setFullScreem,
    fullScreem,
    elements = [],
    connections = [],
    setElementSelected = () => {},
    setElementONUSelected = () => {},
    connectionSelected,
    setConnectionSelected = () => {},
    elementSelected,
    elementONUSelected,
    iconsElements = [],
    mapId,
    invalidElements,
    setSnackbar,
    editMode,
    setEditMode,
    checkChanges,
    setCheckChanges,
    dataONUS,
    boundsCoordinates,
    centerCoordinates = [-10, -48],
    initialZoom,
    isDateRecent,
    collapse,
  } = props;

  const [lockMode, setLockMode] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const [elementsConnected, setElementsConnected] = useState([]);
  const [elementsONUs, setElementsONUs] = useState([]);
  const [openRightBar, setOpenRightBar] = useState(false);
  const [contextMenuFlow, setContextMenuFlow] = useState(null);
  const [openAlertModal, setOpenAlertModal] = useState(null);
  const [openConnection, setOpenConnection] = useState(null);
  const [showLabelEnlace, setShowLabelEnlace] = useState(true);
  const [rulerMode, setRulerMode] = useState(false);
  const [rulerPoint, setRulerPoint] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(!isDateRecent);
  const [focusNodeId, setFocusNodeId] = useState(null);
  const mapRef = useRef(null);
  const didSetInitialZoom = useRef(false);
  const shouldGetZoomRef = useRef(false);

  const theme = useTheme();
  const { config, configLocal, setConfigLocal } = useOutletContext();

  const [markers, setMarkers] = useState([]);
  const [currentClusters, setCurrentClusters] = useState([]);
  const [selectedCtoOnus, setSelectedCtoOnus] = useState([]);

  const isSm = useMediaQuery(theme.breakpoints.down("lg"));

  /* ---------- MEMO ---------- */
  const onusInvalidas = useMemo(() => {
    return (invalidElements ?? []).filter((el) => el.tipoElemento === "onu");
  }, [invalidElements]);

  const filteredMarkers = useMemo(() => {
    let filtered = markers;
    if (configLocal?.esconderOnu) {
      filtered = markers.filter(
        (m) =>
          m.tipoElemento === "cto" ||
          selectedCtoOnus.some((onu) => onu.id === m.id)
      );
    }
    return filtered;
  }, [markers, configLocal?.esconderOnu, selectedCtoOnus]);

  function colorEnlace(power) {
    if (power == null || power == 0 || power === Infinity || !config) {
      return theme.palette.enlaces.offline.main;
    }
    const result = config?.escalas?.ftth
      .filter((scale) => parseFloat(scale?.label) >= power)
      .pop();

    if (!result) return theme.palette.enlaces.offline.main;
    return result?.cor;
  }

  useEffect(() => {
    setSnackbarOpen(!isDateRecent);
  }, [isDateRecent]);

  useEffect(() => {
    const ftthEscalaNum = (config?.escalas?.ftth ?? [])
      .map((e) => parseFloat(e?.label))
      .filter(Number.isFinite)
      .sort((a, b) => a - b);

    const selected = new Set(
      (Array.isArray(configLocal?.filtroPotencia)
        ? configLocal.filtroPotencia
        : []
      ).map((v) => String(v))
    );

    const bucketLabel = (p) => {
      const rx = Number.parseFloat(p);
      if (!Number.isFinite(rx) || ftthEscalaNum.length === 0)
        return "Sem Dados";

      const topo = ftthEscalaNum[ftthEscalaNum.length - 1];
      if (rx > topo) return "Sem Dados";

      const idx = ftthEscalaNum.findIndex((c) => c >= rx);
      const ceil = idx >= 0 ? ftthEscalaNum[idx] : topo;
      return String(ceil);
    };

    const mks = elements.map((element) => {
      const base = element.tipoElemento === "cto" ? ["cto"] : ["house"];

      return {
        ...element,
        position: [
          parseFloat(element?.coordenadas.lat),
          parseFloat(element?.coordenadas.long),
        ],
        iconId: base,
        baseIcon: base,
        tint: colorEnlace(element?.data?.potencia?.rx), // já é "#rrggbb"
        label: element.titulo ?? element.id, // nbsp p/ não quebrar layout
      };
    });

    const filteredMks = mks.filter((m) => {
      if (selected.size === 0) return true;
      const label = bucketLabel(m?.data?.potencia?.rx);
      return selected.has(label);
    });

    setMarkers(filteredMks);
  }, [elements, config, configLocal?.filtroPotencia]);

  const handleClick = useCallback(
    (id) => {
      const found = markers.find((el) => el.id === id);
      setElementSelected((prev) => {
        if (prev?.id === id) return prev;
        return found ?? prev ?? null;
      });

      if (found && found.tipoElemento === "onu") {
        setElementONUSelected(found);

        const ctoTitle = markers.find(
          (m) =>
            m.tipoElemento === "cto" &&
            m.onus?.includes(found.id) &&
            (m.titulo ?? "").trim().toUpperCase() ===
              (found.cto ?? "").trim().toUpperCase()
        );

        if (ctoTitle) {
          const ctoOnus = markers.filter(
            (m) =>
              m.tipoElemento === "onu" &&
              ctoTitle.onus?.includes(m.id) &&
              (m.cto ?? "").trim().toUpperCase() ===
                (ctoTitle.titulo ?? "").trim().toUpperCase()
          );

          setSelectedCtoOnus([ctoTitle, ...ctoOnus]);
        } else {
          setSelectedCtoOnus([found]);
        }
      } else {
        setElementONUSelected(null);
        if (found && found.tipoElemento === "cto") {
          const ctoTitle = (found.titulo ?? "").trim().toUpperCase();
          const ctoOnus = markers.filter(
            (m) =>
              m.tipoElemento === "onu" &&
              found.onus?.includes(m.id) &&
              (m.cto ?? "").trim().toUpperCase() === ctoTitle
          );
          setSelectedCtoOnus(ctoOnus);
        } else {
          setSelectedCtoOnus([]);
        }
      }
    },
    [markers, setElementSelected, configLocal?.esconderOnu]
  );

  const handleBackgroundClick = useCallback(() => {
    setElementSelected(null);
    setFocusNodeId(null);
    setSelectedCtoOnus([]);
  });

  const handleClusterClick = useCallback((cluster) => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    const currentZoom = map.getZoom();
    const newZoom = Math.min(currentZoom + 2, 19);

    map.setView(L.latLng(cluster.center), newZoom);
  }, []);
  /* ---------- TEXTURAS ---------- */
  const texturesReady = useTextures();

  //===============================================================

  useEffect(() => {
    setTimeout(() => {
      if (mapRef.current && mapRef.current.invalidateSize) {
        mapRef.current.invalidateSize();
      }
    }, 300);
  }, [collapse]);

  useEffect(() => {
    if (!mapRef.current) return;
    SetMaxBounds();
    mapRef.current.invalidateSize();
  }, [mapRef.current]);

  useEffect(() => {
    if (!rulerMode) return;

    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        removeLastEl();
      }
    };

    const handleMouseClick = (e) => {
      if (e.button === 2) {
        e.preventDefault();
        removeLastEl();
      }
    };

    const handleContextMenu = (e) => {
      if (rulerMode) {
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("mousedown", handleMouseClick);
    window.addEventListener("contextmenu", handleContextMenu);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("mousedown", handleMouseClick);
      window.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [rulerMode]);

  useEffect(() => {
    if (!elementSelected?.id) setElementSelected(null);
    if (editMode == false) {
      setElementSelected((data) => markers.find((e) => e?.id == data?.id));
    }
    if (editMode === false && elementSelected?.id) {
      const stillThere = markers.some((e) => e?.id === elementSelected.id);
      if (!stillThere) setElementSelected(null);
    }
    setConnectionSelected(null);
    setElementsConnected(null);
  }, [editMode, showLabel, markers, elementSelected?.id]);

  const getSerial = useCallback(
    (id) => (id ?? "").replace(/_onu$/i, "").trim().toUpperCase(),
    []
  );

  const allOnus = useMemo(
    () => [...markers, ...onusInvalidas],
    [markers, onusInvalidas]
  );

  const serial = useMemo(
    () => (dataONUS?.serialOnu ?? "").trim().toUpperCase(),
    [dataONUS?.serialOnu]
  );

  const filteredOnus = useMemo(() => {
    if (!elementSelected?.id) return [];
    if (elementSelected.tipoElemento === "cto") {
      const titulo = (elementSelected.titulo ?? "").trim().toUpperCase();
      return allOnus.filter(
        (el) =>
          elementSelected.onus?.includes(el.id) &&
          (el.cto ?? "").trim().toUpperCase() === titulo
      );
    }

    const ctoFiltered = markers?.find(
      (el) => el.titulo === elementSelected.cto
    );
    return ctoFiltered
      ? allOnus.filter((el) => ctoFiltered.onus?.includes(el.id))
      : [elementSelected];
  }, [elementSelected, allOnus, markers]);

  const mergedOnus = useMemo(() => {
    if (!serial || !dataONUS) return filteredOnus;

    const extra = formatExtraData(dataONUS) ?? {};

    return filteredOnus.map((onu) => {
      const matchOnu = getSerial(onu.id) === serial;
      if (!matchOnu) return onu;

      return {
        ...onu,
        data: {
          ...(onu.data ?? {}),
          ...extra,
        },
      };
    });
  }, [filteredOnus, serial, dataONUS, getSerial]);

  useEffect(() => {
    const cood = elementSelected?.position;
    if (cood?.length) focusZoomCoordenates(cood[0], cood[1]);
  }, [elementSelected?.position]);

  useEffect(() => {
    if (!elementSelected?.id) {
      setElementsONUs([]);
      setElementONUSelected(null);
      return;
    }

    setElementsONUs(mergedOnus);

    const found =
      (serial && mergedOnus.find((o) => getSerial(o.id) === serial)) ||
      mergedOnus.find((o) => o.id === elementSelected.id) ||
      null;
    if (elementSelected.tipoElemento === "onu" && found) {
      setElementONUSelected(found);
    }
  }, [elementSelected?.id, mergedOnus, serial, getSerial]);

  const SetMaxBounds = () => {
    if (!mapRef.current) return;

    var southWest = L.latLng(-89.98155760646617, -180),
      northEast = L.latLng(89.99346179538875, 180);
    var bounds = L.latLngBounds(southWest, northEast);

    mapRef.current.setMaxBounds(bounds);

    return null;
  };
  const clearRuler = () => {
    setRulerPoint([]);
    setRulerMode(false);
  };

  const removeLast = () => {
    setRulerPoint((prev) => {
      if (prev.length === 0) return prev;
      return prev.slice(0, -2);
    });
  };

  const removeLastEl = () => {
    setRulerPoint((prev) => {
      if (prev.length === 0) return prev;
      return prev.slice(0, -1);
    });
  };

  const styles = {
    paperContainer: {
      padding: "16px ",
      display: "flex",
      alignItems: "center",
      position: "relative",
      width: "100%",
      borderRadius: 3,
      mt: 5.5,
    },
    body: {
      width: "100%",
      height: "100%",
      position: "absolute",
      display: "flex",
      flexDirection: "column",
      left: "0px",
      top: "0px",
      //zIndex: 0,
      overflow: "hidden",
    },

    mapInfoStack: {
      padding: "12px 16px",
      width: "250px",
    },
    mapInfoDataStack: {
      padding: "12px 16px",
      width: "120px",
    },
    mapInfoTitle: {
      fontSize: "0.75rem",
      lineHeight: 1.25,
      fontWeight: 600,
      letterSpacing: "0.033em",
      color: "primary.main",
    },
    mapInfoText: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
      fontWeight: 400,
      textTransform: "capitalize",
    },
    fontPopup: {
      margin: "0px",
      fontSize: "10px",
      fontWeight: 500,
      color: theme.palette.color.textEasyMaps,
    },
    titlePopup: {
      margin: "0px",
      fontSize: "12px",
      fontWeight: 500,
      color: theme.palette.color.textEasyMaps,
      width: "100%",
      textAlign: "center",
      marginBottom: "10px",
      whiteSpace: "normal",
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "-webkit-box",
      WebkitLineClamp: 2, // Limite de 2 linhas
      WebkitBoxOrient: "vertical",
    },
  };

  function formatExtraData(obj) {
    return {
      statusContrato: { value: obj.statusContrato ?? "", unit: "" },
      pop: { value: obj.popCliente ?? "", unit: "" },
      desconexoes: { value: obj.desconexoesCliente ?? 0, unit: "x" },
      posicao: { value: obj.posicaoOnu ?? "", unit: "" },
      ip: { value: obj.ipCliente ?? "", unit: "" },
      plano: { value: obj.planoContrato ?? "", unit: "" },
      valorPlano: { value: obj.valorPlano ?? 0, unit: "Reais" },
      localizacao: {
        cep: obj.cepCliente ?? "",
        bairro: obj.bairroCliente ?? "",
        rua: obj.enderecoCliente ?? "",
        numero: obj.numeroEndereco ?? "",
      },
    };
  }

  const formatPoints = (edge) => {
    let nodeStart =
      edge.referenceId === elementSelected?.id
        ? elementSelected
        : [...elements, ...elementsONUs].find((n) => n.id === edge.referenceId);

    let nodeEnd =
      edge.destinationId === elementSelected?.id
        ? elementSelected
        : [...elements, ...elementsONUs].find(
            (n) => n.id === edge.destinationId
          );

    const nodeStartPosition = {
      lat: parseFloat(nodeStart?.coordenadas?.lat),
      long: parseFloat(nodeStart?.coordenadas?.long),
      active: false,
    };
    const nodeEndPosition = {
      lat: parseFloat(nodeEnd?.coordenadas?.lat),
      long: parseFloat(nodeEnd?.coordenadas?.long),
      active: false,
    };

    return [
      nodeStartPosition,
      ...(edge.positionHandlers ?? []).map((a) => ({
        lat: a.y,
        long: a.x,
        active: false,
      })),
      nodeEndPosition,
    ];
  };

  function map_range(value, low1, high1, low2, high2) {
    return parseInt(low2 + ((high2 - low2) * (value - low1)) / (high1 - low1));
  }

  function zoom(action) {
    if (!mapRef.current) return;
    const map = mapRef.current;
    let zoom = map.getZoom();
    // garante que o mapa não fique deslizando
    if (zoom > 19 && action > 0) return;
    if (zoom < 1 && action < 0) return;
    map.setZoom(zoom + action);
  }
  function focusZoom(id) {
    if (!id) return;
    let element = markers.find((e) => e.id == id);

    if (!element) return;
    let cood = element.position;
    if (cood.length) {
      focusZoomCoordenates(cood[0], cood[1]);
      setFocusNodeId(id);
    }
  }
  function focusZoomCoordenates(lat, lng) {
    const forceZoom = shouldGetZoomRef.current;

    if (lat == null || lng == null) return;
    const map = mapRef.current;
    if (!elementSelected)
      forceZoom ? map?.setView([lat, lng], 18) : map?.setView([lat, lng]);
    else
      forceZoom
        ? map?.setView([lat, parseFloat(lng) + 0.0003], 18)
        : map?.setView([lat, parseFloat(lng) + 0.0003]);
    shouldGetZoomRef.current = false;
  }
  useImperativeHandle(ref, () => ({
    focusZoomExt(id) {
      if (!id) return;
      let element = markers.find((e) => e.id == id);
      if (element) {
        shouldGetZoomRef.current = true;
        setElementSelected(element);
      }
    },
  }));

  function Events() {
    const map = useMap();
    const moveTimeout = useRef();
    useMapEvents({
      click(e) {
        if (rulerMode) {
          setRulerPoint((prev) => [...prev, e.latlng]);
        }
      },
      move(e) {
        if (moveTimeout.current) clearTimeout(moveTimeout.current);
        moveTimeout.current = setTimeout(() => {
          const center = map.getBounds().getCenter();
          const zoom = map.getZoom();
          const url = new URL(window.location.href);
          url.searchParams.set("lat", center.lat);
          url.searchParams.set("lng", center.lng);
          url.searchParams.set("zoom", zoom);
          window.history.replaceState({}, "", url);
        }, 200);
      },
    });
    return null;
  }

  const SetInitialZoom = () => {
    const map = useMap();
    useEffect(() => {
      if (!map || didSetInitialZoom.current) return;
      if (initialZoom && centerCoordinates) {
        didSetInitialZoom.current = true;
        return;
      }
      if (Array.isArray(boundsCoordinates) && boundsCoordinates) {
        const dataBounds = L.latLngBounds(boundsCoordinates);
        map.fitBounds(dataBounds, {
          maxZoom: 14,
        });
        didSetInitialZoom.current = true;
      }
    }, [map]);

    return null;
  };

  return (
    <>
      <Box sx={{ overflow: "visible", zIndex: 1202 }}>
        {!isSm && (
          <TitleBar
            title={title}
            id={mapId}
            setSnackbar={setSnackbar}
            center={elementSelected != null || connectionSelected != null}
          />
        )}
      </Box>
      <Box sx={styles.body}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            marginTop: { xs: 5, sm: 0 },
          }}
        >
          {snackbarOpen && (
            <Box
              sx={{
                position: "absolute",
                borderRadius: 2,
                width: { xs: "%100", sm: "calc(100vw - 278px)" },
                ml: { xs: "22vw", lg: "150px" },
                py: 2,
                zIndex: 2,
                display: "flex",
                justifyContent: "center",
                gap: 1.5,
              }}
            >
              <Alert
                severity="warning"
                onClose={() => setSnackbarOpen(false)}
                action={
                  <Close
                    fontSize="small"
                    onClick={() => setSnackbarOpen(false)}
                    sx={{ cursor: "pointer" }}
                  />
                }
                sx={{
                  alignItems: "center",

                  width: {
                    xs: "80vw",
                    xsm: "300px",
                    sm: "300px",
                    md: "300px",
                    mdl: "300px",
                    lg: "400px",
                    xl: "635px",
                  },
                }}
              >
                Os dados do mapa estão desatualizados. Por favor, entre em{" "}
                <Link
                  href="https://wa.me/5584994581475"
                  color={theme.palette.warning.main}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  contato com o suporte.
                </Link>
              </Alert>
            </Box>
          )}

          <ToolBarMaps
            setEasyMapsConfig={setConfigLocal}
            easyMapsConfig={configLocal}
            editMode={editMode}
            setEditMode={setEditMode}
            fullScreem={fullScreem}
            setFullScreem={setFullScreem}
            showLabel={showLabel}
            setShowLabel={setShowLabel}
            lockMode={lockMode}
            setLockMode={setLockMode}
            setShowLabelEnlace={setShowLabelEnlace}
            showLabelEnlace={showLabelEnlace}
            setOpenAlertModal={setOpenAlertModal}
            checkChanges={checkChanges}
            setShowRuler={setRulerMode}
            setRulerPoint={setRulerPoint}
            showRuler={rulerMode}
            tipo={0}
            isFtth={true}
            config={config}
            collapse={collapse}
          >
            <ToolBarMaps.InputZoomGeo zoom={zoom} />
          </ToolBarMaps>

          {!editMode && (
            <DescriptionBar
              key={"descrptionbar"}
              variant="ftth"
              elementSelected={!!elementSelected}
            />
          )}

          <MapContainer
            //maxBoundsViscosity={1}
            //worldCopyJump={true}
            fullscreenControl={false}
            ref={mapRef}
            key={`mapaSingle`}
            id="map"
            center={centerCoordinates}
            zoom={initialZoom ?? 4}
            // inertia={true}
            // boxZoom={true}
            minZoom={3}
            // maxZoom={19}
            // fadeAnimation={false}
            // zoomControl={false}
            // easeLinearity={0.6}
            // doubleClickZoom={false}
            //  enableHighAccuracy={true}
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              zIndex: 0,
            }}
          >
            <TileLayer
              url={
                theme.palette.mode == "dark"
                  ? config?.mapaTile?.dark ||
                    "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  : config?.mapaTile?.light ||
                    "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              }
              //url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              //url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
              detectRetina={true}
            />
            <Events />
            {rulerMode && (
              <RulerComponent
                rulerPoints={rulerPoint}
                removeLast={removeLast}
                onClearRuler={clearRuler}
              />
            )}
            <SetInitialZoom />
            {/* <ClickDispatcher
              markers={markers}
              onClick={handleClick}
              onBackgroundClick={handleBackgroundClick}
              clusters={currentClusters}
              enableClustering={mapRef?.current?.getZoom() <= 17}
              onClusterClick={handleClusterClick}
            /> */}
            {texturesReady && (
              <PixiOverlay
                markers={filteredMarkers}
                enableClustering={mapRef?.current?.getZoom() <= 17}
                onClustersChange={setCurrentClusters}
                elementSelected={elementSelected}
                selectedCtoOnus={selectedCtoOnus}
                focusNodeId={focusNodeId}
                onClick={handleClick}
                onBackgroundClick={handleBackgroundClick}
                onClusterClick={handleClusterClick}
                configLocal={configLocal}
              />
            )}
            {elementSelected &&
              connections
                .filter((conn) => {
                  const onu = elementsONUs.find(
                    (ele) => ele.id === conn.destinationId
                  );

                  return (
                    conn.referenceId ===
                      (elementSelected.tipoElemento === "cto"
                        ? elementSelected.id
                        : `${elementSelected?.cto?.replace(/\s/g, "")}_cto`) &&
                    onu &&
                    onu?.coordenadas?.lat != null &&
                    onu?.coordenadas?.long != null
                  );
                })
                .map((conn, index) => {
                  let position = formatPoints(conn);
                  if (!editMode) {
                    let destination = elementsONUs.find(
                      (a) => a.id == conn.destinationId
                    );
                    return (
                      <PolylineCustom
                        darkMode={theme.palette.mode == "dark"}
                        configLocal={configLocal}
                        variant="ftth"
                        percent={destination?.data?.potencia?.rx}
                        host={
                          [...elementsONUs].find(
                            (element) => element.id == conn.destinationId
                          )?.titulo
                        }
                        data={{
                          ...conn?.data,
                          powerRx: destination?.data?.potencia?.rx,
                          powerTx: destination?.data?.potencia?.tx,
                        }}
                        isDown={!!!destination?.data?.status.value}
                        key={conn.id + "selected" + index}
                        positions={position.map((a) => [
                          a.lat || 0,
                          a.long || 0,
                        ])}
                        animation={"pontilhada"}
                        onClick={(e) => {
                          L.DomEvent.stopPropagation(e);
                          let elemento = elementsONUs.find(
                            (ele) => ele.id == conn.destinationId
                          );
                          focusZoom(elemento.id);
                          setOpenConnection(elemento.id);
                        }}
                      />
                    );
                  }
                })}
          </MapContainer>

          <RightBarEasyMapsOnus
            mapId={mapId}
            setElementONUSelected={setElementONUSelected}
            elementONUSelected={elementONUSelected}
            setFocusNodeId={setFocusNodeId}
            focusZoom={focusZoom}
            open={openRightBar}
            setOpen={setOpenRightBar}
            elements={elements}
            connections={connections}
            setElement={setElementSelected}
            setConnections={setElementsConnected}
            iconsElement={iconsElements}
            element={!editMode && elementSelected?.id ? elementSelected : null}
            fullScreem={fullScreem}
            openConnection={openConnection}
            setOpenConnection={setOpenConnection}
            link={`/easyMaps/mapa/${
              elementSelected?.mapRef?.tipo === 0 ? "geo" : "top"
            }/${elementSelected?.mapRefId ?? ""}`}
            elementsONUs={elementsONUs}
          />
        </Box>

        <ContextMenu
          contextMenu={contextMenuFlow}
          setContextMenu={setContextMenuFlow}
          editMode={editMode}
          setEditMode={setEditMode}
        />

        <AlertModal
          openModal={openAlertModal?.open ?? false}
          setOpenModal={(data) => setOpenAlertModal({ open: data })}
          buttonText={openAlertModal?.buttonText}
          buttonCancelText={openAlertModal?.buttonCancelText}
          confirmar={false}
          handleButton={() => {
            setCheckChanges(false);
            setOpenAlertModal({ open: false });
            if (openAlertModal.callback) {
              openAlertModal.callback(...(openAlertModal?.params || []));
              return;
            }
            setElementSelected(null);
            setElementONUSelected(null);
            setConnectionSelected(null);
          }}
          severity={openAlertModal?.severity}
          singleButton={false}
          textContent={openAlertModal?.text}
        />
      </Box>
    </>
  );
});

export default LeafletMapa;
